/** @jsx jsx */
import React, { useMemo, useCallback } from 'react';
import { jsx } from '@compiled/react';
import AppIcon from '@atlaskit/icon/core/app';
import SettingsIcon from '@atlaskit/icon/core/settings';
import LinkItem from '@atlaskit/menu/link-item';
import Section from '@atlaskit/menu/section';
import { token } from '@atlaskit/tokens';
import { expVal } from '@atlassian/jira-feature-experiments';
import { fg } from '@atlassian/jira-feature-gating';
import { useIntl, defineMessage } from '@atlassian/jira-intl';
import { getATLContextDomain } from '@atlassian/atl-context';
import {
	FIXED_ITEM_ACTION,
	FIXED_ITEM_BUTTON,
} from '@jira/navigation-apps__sidebar-nav4-analytics/src/common/constants/analytics/component-type.tsx';
import {
	LEVEL_ONE,
	LEVEL_TWO,
} from '@jira/navigation-apps__sidebar-nav4-analytics/src/common/constants/analytics/level.tsx';
import { SECTION_ITEM_APPS } from '@jira/navigation-apps__sidebar-nav4-analytics/src/common/constants/analytics/section-type.tsx';
import { MENU_ID_ANALYTIC_EVENT_IDS } from '@jira/navigation-apps__sidebar-nav4-analytics/src/common/constants/analytics/side-nav.tsx';
import { MENU_ID_APPS } from '@jira/navigation-apps__sidebar-nav4-context/src/common/constants.tsx';
import {
	type OnCloseProps,
	L1_MENU_ID,
} from '@jira/navigation-apps__sidebar-nav4-sidebars-common-core/src/common/types.tsx';
import { useStopPropagation } from '@jira/navigation-apps__sidebar-nav4-sidebars-common-core/src/common/utils/use-stop-propagation/index.tsx';
import { HideL1MenuItemSection } from '@jira/navigation-apps__sidebar-nav4-sidebars-common-core/src/ui/hide-l1-menu-item-action/index.tsx';
import { MoreNavMenuButton } from '@jira/navigation-apps__sidebar-nav4-sidebars-common-core/src/ui/more-nav-menu-button/main.tsx';
import type { MoreNavMenuButtonMenuItemsProps } from '@jira/navigation-apps__sidebar-nav4-sidebars-common-core/src/ui/more-nav-menu-button/submenu/index.tsx';
import { getAppRequestsLink } from '@jira/platform__services-marketplace-url-resolver/src/index.tsx';
import { fireUIAnalytics, useAnalyticsEvents } from '@atlassian/jira-product-analytics-bridge';
import { useTenantContext } from '@atlassian/jira-tenant-context-controller/src/components/tenant-context/index.tsx';

const manageApps = defineMessage({
	id: 'navigation-apps-sidebar-nav4-sidebars-content-apps.common.section-actions.manage-apps',

	defaultMessage: 'Manage apps',
	description:
		'Link to go manage your apps in admin settings. It is shown in the dropdown next to the Apps menu on a global sidebar',
});

const appAuditLogs = defineMessage({
	id: 'navigation-apps-sidebar-nav4-sidebars-content-apps.common.section-actions.app-audit-logs',

	defaultMessage: 'App audit logs',
	description:
		'Link to go to app audit logs in admin settings. It is shown in the dropdown next to the Apps menu on a global sidebar',
});

const viewAppRequests = defineMessage({
	id: 'navigation-apps-sidebar-nav4-sidebars-content-apps.common.section-actions.view-app-requests',

	defaultMessage: 'View app requests',
	description:
		'Link to go to app requests in admin settings. It is shown in the dropdown next to the Apps menu on a global sidebar',
});

const apps = defineMessage({
	id: 'navigation-apps-sidebar-nav4-sidebars-content-apps.common.section-actions.apps',
	defaultMessage: 'Apps',
	description:
		'Name of the Apps section in the sidebar. Used for accessibility labels and tooltips.',
});

export function SectionActions({ onClose }: OnCloseProps): React.JSX.Element {
	const tenantContext = useTenantContext();
	const { isAdmin, isSiteAdmin } = tenantContext;
	const { createAnalyticsEvent } = useAnalyticsEvents();
	const { formatMessage } = useIntl();

	const showManageApps = isAdmin || isSiteAdmin;
	const showAppAuditLogs = fg('show_app_audit_logs_upm_dep') && (isAdmin || isSiteAdmin);
	const showViewAppRequests = expVal('enduser-emcee-discover', 'value', true) && isSiteAdmin;

	const fireOpenEvent = useCallback(() => {
		fireUIAnalytics(createAnalyticsEvent({}), {
			action: 'clicked',
			actionSubject: 'button',
			actionSubjectId: MENU_ID_ANALYTIC_EVENT_IDS[MENU_ID_APPS],
			attributes: {
				level: LEVEL_ONE,
				componentType: FIXED_ITEM_BUTTON,
				itemId: 'meatballDropdown',
			},
		});
	}, [createAnalyticsEvent]);

	const showActions = showManageApps || showViewAppRequests;
	if (!showActions && fg('navx-global-side-nav')) {
		return <></>; // Function return-type is JSX, rather than ReactNode, so gotta do this
	}

	return (
		<MoreNavMenuButton
			MenuItems={(props) => (
				<MoreMenuItems
					showManageApps={showManageApps}
					showViewAppRequests={showViewAppRequests}
					showAppAuditLogs={showAppAuditLogs}
					{...props}
				/>
			)}
			onOpen={fireOpenEvent}
			onClose={onClose}
			name={formatMessage(apps)}
		/>
	);
}

type MoreMenuItemsProps = {
	showManageApps: boolean;
	showViewAppRequests: boolean;
	showAppAuditLogs?: boolean;
} & MoreNavMenuButtonMenuItemsProps;

function MoreMenuItems({
	onClose,
	showManageApps,
	showViewAppRequests,
	showAppAuditLogs,
}: MoreMenuItemsProps) {
	const tenantContext = useTenantContext();
	const { cloudId } = tenantContext;

	const { formatMessage } = useIntl();
	const { createAnalyticsEvent } = useAnalyticsEvents();

	const adminHubAppRequestsUrl = `https://${getATLContextDomain('admin')}/s/${cloudId}/user-connected-apps/tab/requested`;

	const viewAppRequestsLink = useMemo(
		() =>
			fg('should_redirect_to_admin-hub_for_app_requests')
				? adminHubAppRequestsUrl
				: getAppRequestsLink(),
		[adminHubAppRequestsUrl],
	);
	const onCloseWithStopPropagation = useStopPropagation(onClose);
	const showTopSection = showManageApps || showViewAppRequests;

	const fireClickEvent = useCallback(
		(itemId: 'manageApps' | 'viewAppRequests') => {
			fireUIAnalytics(createAnalyticsEvent({}), {
				action: 'clicked',
				actionSubject: 'dropdownItem',
				actionSubjectId: MENU_ID_ANALYTIC_EVENT_IDS[MENU_ID_APPS],
				attributes: {
					level: LEVEL_TWO,
					componentType: FIXED_ITEM_ACTION,
					itemId,
					parentItemId: SECTION_ITEM_APPS,
				},
			});
		},
		[createAnalyticsEvent],
	);

	return (
		<>
			{showTopSection && (
				<Section isList={fg('ditto-a11y-fix-nav-menu-items')}>
					{showManageApps && (
						<LinkItem
							iconBefore={<SettingsIcon label="" spacing="spacious" color={token('color.icon')} />}
							href="/plugins/servlet/upm"
							target="_blank"
							onClick={(...args) => {
								fireClickEvent('manageApps');
								onCloseWithStopPropagation(...args);
							}}
						>
							{formatMessage(manageApps)}
						</LinkItem>
					)}
					{showAppAuditLogs && (
						<LinkItem
							iconBefore={<SettingsIcon label="" spacing="spacious" color={token('color.icon')} />}
							href="/plugins/servlet/upm/log"
							onClick={(...args) => {
								onCloseWithStopPropagation(...args);
							}}
							target="_blank"
						>
							{formatMessage(appAuditLogs)}
						</LinkItem>
					)}
					{showViewAppRequests && (
						<LinkItem
							iconBefore={<AppIcon label="" spacing="spacious" color={token('color.icon')} />}
							href={viewAppRequestsLink}
							target="_blank"
							onClick={(...args) => {
								fireClickEvent('viewAppRequests');
								onCloseWithStopPropagation(...args);
							}}
						>
							{formatMessage(viewAppRequests)}
						</LinkItem>
					)}
				</Section>
			)}
			{!fg('navx-global-side-nav') && (
				<HideL1MenuItemSection menuId={L1_MENU_ID.APPS} hasSeparator={showTopSection} />
			)}
		</>
	);
}
